import { useState, useRef } from 'react'
import { getS3UploadUrl } from '@/api/avpulse-api'
import { useAuth } from '@/contexts/AuthContext'

interface UseFileUploadReturn {
  upload: (file: File) => Promise<string>
  uploadPendingFile: (file: File) => Promise<string>
  cancel: () => void
  isUploading: boolean
  error: string | null
}

export const useFileUpload = (): UseFileUploadReturn => {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const { isAuthenticated } = useAuth()

  const cancel = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
      setIsUploading(false)
    }
  }

  const generateS3UploadUrl = async (
    file: File,
  ): Promise<{
    s3_uri: string
    upload_url: string
    file_type: string
    filename: string
  }> => {
    const fileType = file.type
    if (!fileType.startsWith('image/') && !fileType.startsWith('video/')) {
      throw new Error(
        'Invalid file type. Please upload an image or video file.',
      )
    }

    return getS3UploadUrl(file.name, fileType)
  }

  const uploadToS3 = async (file: File): Promise<string> => {
    abortControllerRef.current = new AbortController()

    const response = await generateS3UploadUrl(file)
    const { s3_uri, upload_url } = response

    const uploadResponse = await fetch(upload_url, {
      method: 'PUT',
      body: file,
      signal: abortControllerRef.current.signal,
    })

    if (!uploadResponse.ok) {
      throw new Error('Failed to upload file')
    }

    abortControllerRef.current = null
    return s3_uri
  }

  const upload = async (file: File): Promise<string> => {
    try {
      setIsUploading(true)
      setError(null)

      // If user is not authenticated, return a local identifier
      if (!isAuthenticated) {
        setIsUploading(false)
        return `local://${file.name}`
      }

      // If authenticated, proceed with S3 upload
      const s3Uri = await uploadToS3(file)
      return s3Uri
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Upload cancelled')
        throw new Error('Upload cancelled')
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to upload file'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsUploading(false)
      abortControllerRef.current = null
    }
  }

  // Function to upload a pending file after login
  const uploadPendingFile = async (file: File): Promise<string> => {
    try {
      setIsUploading(true)
      setError(null)

      const s3Uri = await uploadToS3(file)
      return s3Uri
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Upload cancelled')
        throw new Error('Upload cancelled')
      }

      const errorMessage =
        err instanceof Error ? err.message : 'Failed to upload file'
      setError(errorMessage)
      throw new Error(errorMessage)
    } finally {
      setIsUploading(false)
      abortControllerRef.current = null
    }
  }

  return { upload, uploadPendingFile, cancel, isUploading, error }
}
